"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { usePathname } from "next/navigation";
import { readAdvertisingConsent } from "@/components/analytics/browser-attribution";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";
import { isSignedUrlSurfacePath } from "@/lib/analytics/signed-url-surfaces";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import {
  isCurrentUrlSafeForMicrosoftUet,
  MICROSOFT_UET_UNSUPPRESSED_EVENT,
  pushMicrosoftUetCommand,
  shouldLoadMicrosoftUet,
} from "@/lib/analytics/microsoft-uet";
import { ANALYTICS_LOCATION_CHANGE_EVENT } from "@/lib/analytics/suppressed-paths";

function subscribeNoop() {
  return () => {};
}

function subscribeToConsent(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  return subscribeToBrowserConsentCategory("advertising", cb);
}

function subscribeToLocation(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, cb);
  return () => window.removeEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, cb);
}

function getServerLocationSnapshot(): string {
  return "";
}

function getClientLocationSnapshot(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

function getServerConsentSnapshot(): "accepted" | "declined" | null {
  return null;
}

function getClientConsentSnapshot(): "accepted" | "declined" | null {
  return readAdvertisingConsent();
}

function getServerMicrosoftUetEnabled() {
  return shouldLoadMicrosoftUet();
}

function getClientMicrosoftUetEnabled() {
  if (typeof window === "undefined") return getServerMicrosoftUetEnabled();
  return shouldLoadMicrosoftUet(
    process.env.NEXT_PUBLIC_APP_BASE_URL,
    window.location.hostname,
  );
}

function ensureMicrosoftUetQueue(): MicrosoftUetQueue {
  if (!window.uetq) window.uetq = [];
  return window.uetq;
}

function initialiseMicrosoftUet(tagId: string): boolean {
  if (!window.UET) return false;
  const currentQueue = ensureMicrosoftUetQueue();

  if (Array.isArray(currentQueue)) {
    window.uetq = new window.UET({
      ti: tagId,
      q: currentQueue,
      enableAutoSpaTracking: false,
      enableAutoConsent: false,
    });
  }

  return true;
}

/**
 * Direct Microsoft Advertising UET loader.
 *
 * UET deliberately uses manual SPA page loads. That lets the shared analytics
 * suppression boundary decide whether a URL is safe before Microsoft receives
 * it. The library is not fetched until optional-cookie consent is accepted.
 */
export function MicrosoftUet() {
  const tagId = process.env.NEXT_PUBLIC_MICROSOFT_UET_TAG_ID?.trim() ?? "";
  const envEnabled = useSyncExternalStore(
    subscribeNoop,
    getClientMicrosoftUetEnabled,
    getServerMicrosoftUetEnabled,
  );
  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );
  const pathname = usePathname();
  const locationKey = useSyncExternalStore(
    subscribeToLocation,
    getClientLocationSnapshot,
    getServerLocationSnapshot,
  );
  const suppressed = useAnalyticsSuppressed(pathname);
  // #2326 round 22: UET automatic-event collection is not DOM-attribute-
  // safe — same suppression as GA/Clarity on the signed-URL surfaces.
  const enabled =
    envEnabled && !suppressed && !isSignedUrlSurfacePath(pathname);
  const [libraryReady, setLibraryReady] = useState(false);
  const lastPageKey = useRef<string | null>(null);

  // #2326 round 23: the DENIAL must run PRE-PAINT (see the GA component's
  // layout effect) so an SPA navigation into a signed-URL surface cannot
  // leave automatic-event collection granted across the paint interval.
  useLayoutEffect(() => {
    if (enabled) return;
    window.__tsbMicrosoftUetSuppressed = true;
    pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "denied",
    });
  }, [enabled]);

  useEffect(() => {
    if (!enabled || consent !== "accepted" || !tagId) {
      window.__tsbMicrosoftUetSuppressed = true;
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "denied",
      });
      lastPageKey.current = null;
      return;
    }

    window.__tsbMicrosoftUetSuppressed = false;
    const queue = ensureMicrosoftUetQueue();
    if (Array.isArray(queue)) {
      queue.push("consent", "default", { ad_storage: "denied" });
    }
    pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "granted",
    });
    // Signal only after granted is queued: mount-time events that were
    // swallowed while the first effect pass ran suppressed (hydration-lagged
    // consent snapshot) retry on this event with consent-before-event
    // ordering intact.
    window.dispatchEvent(new Event(MICROSOFT_UET_UNSUPPRESSED_EVENT));

    let active = true;
    let script = document.getElementById(
      "microsoft-uet-lib",
    ) as HTMLScriptElement | null;

    function onLoad() {
      if (!script) return;
      script.dataset.loaded = "true";
      if (
        !active ||
        readAdvertisingConsent() !== "accepted" ||
        !isCurrentUrlSafeForMicrosoftUet()
      ) {
        pushMicrosoftUetCommand("consent", "update", {
          ad_storage: "denied",
        });
        return;
      }
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "granted",
      });
      setLibraryReady(initialiseMicrosoftUet(tagId));
    }

    if (script?.dataset.loaded === "true") {
      queueMicrotask(() => {
        if (active) setLibraryReady(initialiseMicrosoftUet(tagId));
      });
    } else if (!script) {
      script = document.createElement("script");
      script.id = "microsoft-uet-lib";
      script.async = true;
      script.src = "https://bat.bing.com/bat.js";
      script.addEventListener("load", onLoad, { once: true });
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      active = false;
      script?.removeEventListener("load", onLoad);
      if (script?.dataset.loaded !== "true") script?.remove();
      window.__tsbMicrosoftUetSuppressed = true;
      pushMicrosoftUetCommand("consent", "update", {
        ad_storage: "denied",
      });
      lastPageKey.current = null;
    };
  }, [consent, enabled, tagId]);

  useEffect(() => {
    if (
      !libraryReady ||
      !enabled ||
      consent !== "accepted" ||
      !isCurrentUrlSafeForMicrosoftUet()
    ) {
      return;
    }

    const pageKey = locationKey || `${pathname ?? ""}${window.location.search}`;
    if (lastPageKey.current === pageKey) return;

    pushMicrosoftUetCommand("consent", "update", {
      ad_storage: "granted",
    });
    pushMicrosoftUetCommand("pageLoad");
    lastPageKey.current = pageKey;
  }, [consent, enabled, libraryReady, locationKey, pathname]);

  return null;
}
